import React, { useCallback, useEffect, useRef } from "react";
import { personalInfo } from "../data/mock";

const INTRO_VIDEO_SRC =
  process.env.REACT_APP_INTRO_VIDEO_URL || personalInfo.introVideoUrl || "/intro-video.mp4";

export const INTRO_VIDEO_EVENT = "vedant:open-intro";

const IntroVideoSection = ({ onInViewChange }) => {
  const videoRef = useRef(null);
  const sectionRef = useRef(null);

  const scrollToHero = useCallback(() => {
    document.getElementById("hero")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const replayIntro = useCallback(() => {
    const video = videoRef.current;
    document.getElementById("intro-video")?.scrollIntoView({ behavior: "smooth", block: "start" });
    if (!video) return;
    video.currentTime = 0;
    video.muted = false;
    video.volume = 1;
    video.play().catch(() => {});
  }, []);

  const tryAutoplay = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;
    video.volume = 1;
    video.muted = false;
    try {
      await video.play();
    } catch {
      try {
        video.muted = true;
        await video.play();
        video.muted = false;
      } catch {
        /* browser blocked autoplay */
      }
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(tryAutoplay, 350);
    return () => clearTimeout(timer);
  }, [tryAutoplay]);

  useEffect(() => {
    window.addEventListener(INTRO_VIDEO_EVENT, replayIntro);
    return () => window.removeEventListener(INTRO_VIDEO_EVENT, replayIntro);
  }, [replayIntro]);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section || !onInViewChange) return;

    const observer = new IntersectionObserver(
      ([entry]) => onInViewChange(entry.isIntersecting && entry.intersectionRatio > 0.55),
      { threshold: [0, 0.55, 1] }
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, [onInViewChange]);

  const handleEnded = () => {
    setTimeout(scrollToHero, 500);
  };

  return (
    <section
      id="intro-video"
      ref={sectionRef}
      className="relative w-full h-screen min-h-[100dvh] bg-black overflow-hidden"
    >
      <video
        ref={videoRef}
        src={INTRO_VIDEO_SRC}
        className="absolute inset-0 w-full h-full object-cover"
        autoPlay
        playsInline
        preload="auto"
        onEnded={handleEnded}
        onPlaying={(e) => {
          e.currentTarget.muted = false;
          e.currentTarget.volume = 1;
        }}
        onClick={tryAutoplay}
      />
    </section>
  );
};

export const openIntroVideo = () => {
  window.dispatchEvent(new CustomEvent(INTRO_VIDEO_EVENT));
};

export default IntroVideoSection;
